import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Modal,
  Form,
  message,
  Tag,
  Space,
  Card,
  Row,
  Col,
  DatePicker,
  Tooltip,
  Popconfirm,
  Typography,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  ToolOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { interventionsService } from '../services/interventionsService';
import { customersService, type Customer } from '../services/customersService';
import { techniciansService, type Technician } from '../services/techniciansService';
import { contractsService, type Contract } from '../services/contractsService';

const { Title } = Typography;
const { TextArea } = Input;

interface Depannage {
  id: number;
  title: string;
  description: string;
  address?: string; // Optionnel car stocké avec le client
  priority: 'low' | 'normal' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_date?: string;
  created_at: string;
  customer_id?: number;
  technician_id?: number;
  contract_id?: number;
  diagnostic_fee?: number;
  is_free_diagnosis?: boolean;
  customer?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
  };
  technician?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  intervention_type?: string;
}

const DepannagePage: React.FC = () => {
  const [depannages, setDepannages] = useState<Depannage[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDepannage, setSelectedDepannage] = useState<Depannage | null>(null);
  const [form] = Form.useForm();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [createClientModal, setCreateClientModal] = useState(false);
  const [clientForm] = Form.useForm();

  const statusMap = {
    pending: { label: 'En attente', color: 'orange' },
    assigned: { label: 'Assigné', color: 'blue' },
    in_progress: { label: 'En cours', color: 'processing' },
    completed: { label: 'Terminé', color: 'success' },
    cancelled: { label: 'Annulé', color: 'error' }
  };

  const priorityMap = {
    low: { label: 'Faible', color: 'green' },
    normal: { label: 'Normale', color: 'blue' },
    medium: { label: 'Moyenne', color: 'orange' },
    high: { label: 'Élevée', color: 'red' },
    critical: { label: 'Critique', color: 'magenta' }
  };

  const equipmentTypes = [
    'Chaudière',
    'Pompe à chaleur',
    'Climatisation',
    'Chauffe-eau',
    'Radiateur',
    'Ventilation',
    'Autre'
  ];

  useEffect(() => {
    loadDepannages();
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      setLoadingOptions(true);
      const [customersRes, techniciansRes, contractsRes] = await Promise.all([
        customersService.getCustomers({ limit: 1000 }),
        techniciansService.getTechnicians({ limit: 1000 }),
        contractsService.getContracts({ limit: 1000, status: 'active' })
      ]);
      
      setCustomers(customersRes.data.customers || []);
      setTechnicians(techniciansRes.data || []);
      setContracts(contractsRes.data.contracts || []);
    } catch (error) {
      console.error('Erreur lors du chargement des options:', error);
      message.error('Erreur lors du chargement des listes');
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadDepannages = async () => {
    try {
      setLoading(true);
      const response = await interventionsService.getInterventions({ limit: 1000 });
      const interventions = response.data.interventions || [];
      
      // Mapper les interventions vers le format Depannage
      const mappedData: Depannage[] = interventions.map((intervention: any) => ({
        id: intervention.id,
        title: intervention.title,
        description: intervention.description,
        address: intervention.address, // Peut être null
        priority: intervention.priority,
        status: intervention.status,
        scheduled_date: intervention.scheduled_date,
        created_at: intervention.created_at,
        customer_id: intervention.customer_id,
        technician_id: intervention.technician_id,
        contract_id: intervention.contract_id,
        diagnostic_fee: intervention.diagnostic_fee,
        is_free_diagnosis: intervention.is_free_diagnosis,
        customer: intervention.customer,
        technician: intervention.technician,
        intervention_type: intervention.intervention_type
      }));
      
      setDepannages(mappedData);
    } catch (error) {
      message.error('Erreur lors du chargement des interventions');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    form.resetFields();
    setOpenModal(true);
  };

  const handleEdit = (record: Depannage) => {
    setEditMode(true);
    setSelectedDepannage(record);
    form.setFieldsValue({
      ...record,
      scheduled_date: record.scheduled_date ? dayjs(record.scheduled_date) : null
    });
    setOpenModal(true);
  };

  const handleView = (record: Depannage) => {
    setSelectedDepannage(record);
    setViewModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await interventionsService.deleteIntervention(id);
      message.success('Intervention supprimée avec succès');
      loadDepannages();
    } catch (error) {
      message.error('Erreur lors de la suppression');
      console.error('Erreur:', error);
    }
  };

  const handleCreateClient = async (values: any) => {
    try {
      setLoading(true);
      const newClient = await customersService.createCustomer({
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email || undefined, // Email optionnel
        phone: values.phone,
        address: values.address || undefined,
        city: values.city || undefined,
        commune: values.commune || undefined,
      });
      
      message.success('Client créé avec succès');
      
      // Recharger la liste des clients
      await loadOptions();
      
      // Pré-sélectionner le nouveau client dans le formulaire
      form.setFieldsValue({ customer_id: newClient.user_id });
      
      // Fermer la modal et réinitialiser le formulaire
      setCreateClientModal(false);
      clientForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erreur lors de la création du client');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const formData = {
        title: values.title,
        description: values.description,
        priority: values.priority,
        status: values.status || 'pending',
        scheduled_date: values.scheduled_date ? values.scheduled_date.toISOString() : dayjs().add(1, 'day').toISOString(),
        customer_id: values.customer_id,
        technician_id: values.technician_id || null,
        intervention_type: values.intervention_type,
        contract_id: values.contract_id || null, // Si présent, diagnostic gratuit
        product_id: 1 // Valeur par défaut requise par l'API
      };

      if (editMode && selectedDepannage) {
        await interventionsService.updateIntervention(selectedDepannage.id, formData);
        message.success('Intervention modifiée avec succès');
      } else {
        await interventionsService.createIntervention(formData);
        message.success('Intervention créée avec succès');
      }

      setOpenModal(false);
      loadDepannages();
      form.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<Depannage> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: (a, b) => a.id - b.id
    },
    {
      title: 'Client',
      key: 'client',
      render: (_, record) => {
        const customerName = record.customer 
          ? `${record.customer.first_name} ${record.customer.last_name}`
          : 'Client inconnu';
        const customerEmail = record.customer?.email || '-';
        return (
          <Space direction="vertical" size={0}>
            <strong>{customerName}</strong>
            <span style={{ fontSize: '12px', color: '#666' }}>
              {customerEmail}
            </span>
          </Space>
        );
      }
    },
    {
      title: 'Type',
      dataIndex: 'intervention_type',
      key: 'intervention_type',
      render: (type) => type ? (
        <Tag icon={<ToolOutlined />} color="blue">{type}</Tag>
      ) : <Tag color="default">Non spécifié</Tag>,
      filters: equipmentTypes.map(type => ({ text: type, value: type })),
      onFilter: (value, record) => record.intervention_type === value
    },
    {
      title: 'Titre',
      dataIndex: 'title',
      key: 'title',
      ellipsis: { showTitle: false },
      render: (title, record) => (
        <Tooltip title={`${title} - ${record.description}`}>
          {title.length > 40 ? `${title.substring(0, 40)}...` : title}
        </Tooltip>
      )
    },
    {
      title: 'Priorité',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const priorityInfo = priorityMap[priority as keyof typeof priorityMap];
        if (!priorityInfo) {
          return <Tag color="default">{priority || 'Non définie'}</Tag>;
        }
        return (
          <Tag color={priorityInfo.color}>
            {priorityInfo.label}
          </Tag>
        );
      },
      filters: Object.keys(priorityMap).map(key => ({
        text: priorityMap[key as keyof typeof priorityMap].label,
        value: key
      })),
      onFilter: (value, record) => record.priority === value,
      sorter: (a, b) => {
        const order = { critical: 5, high: 4, medium: 3, normal: 2, low: 1 };
        return (order[a.priority as keyof typeof order] || 0) - (order[b.priority as keyof typeof order] || 0);
      }
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusInfo = statusMap[status as keyof typeof statusMap];
        if (!statusInfo) {
          return <Tag color="default">{status || 'Inconnu'}</Tag>;
        }
        return (
          <Tag color={statusInfo.color}>
            {statusInfo.label}
          </Tag>
        );
      },
      filters: Object.keys(statusMap).map(key => ({
        text: statusMap[key as keyof typeof statusMap].label,
        value: key
      })),
      onFilter: (value, record) => record.status === value
    },
    {
      title: 'Technicien',
      key: 'technician',
      render: (_, record) => {
        const tech = record.technician || (record.technician_id ? technicians.find(t => Number(t.id) === Number(record.technician_id)) : null);
        if (tech && (tech.first_name || tech.last_name)) {
          return `${tech.first_name || ''} ${tech.last_name || ''}`.trim();
        }
        if (record.technician_id) {
          return <Tag color="blue">Technicien #{record.technician_id}</Tag>;
        }
        return <Tag color="default">Non assigné</Tag>;
      }
    },
    {
      title: 'Diagnostic',
      key: 'diagnostic',
      render: (_, record) => {
        const isFree = record.is_free_diagnosis || Number(record.diagnostic_fee) === 0;
        if (isFree) {
          return <Tag color="green">GRATUIT</Tag>;
        }
        const fee = Number(record.diagnostic_fee) || 4000;
        return (
          <Tooltip title="Client sans contrat d'entretien">
            <Tag color="gold">{fee.toLocaleString()} FCFA</Tag>
          </Tooltip>
        );
      },
      filters: [
        { text: 'Gratuit', value: 'free' },
        { text: 'Payant', value: 'paid' }
      ],
      onFilter: (value, record) => {
        if (value === 'free') return record.is_free_diagnosis === true;
        return record.is_free_diagnosis === false;
      }
    },
    {
      title: 'Date planifiée',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-',
      sorter: (a, b) => dayjs(a.scheduled_date || 0).unix() - dayjs(b.scheduled_date || 0).unix()
    },
    {
      title: 'Date création',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix()
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Voir">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer ce dépannage ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const getStatistics = () => {
    return {
      total: depannages.length,
      pending: depannages.filter(d => d.status === 'pending').length,
      in_progress: depannages.filter(d => d.status === 'in_progress' || d.status === 'assigned').length,
      completed: depannages.filter(d => d.status === 'completed').length,
      critical: depannages.filter(d => d.priority === 'critical' || d.priority === 'high').length
    };
  };

  const stats = getStatistics();

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Title level={3} style={{ margin: 0 }}>
                <ToolOutlined /> Gestion des Dépannages
              </Title>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadDepannages}
                  loading={loading}
                >
                  Actualiser
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  Nouveau Dépannage
                </Button>
              </Space>
            </Space>
          </Col>
        </Row>

        {/* Statistiques */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff' }}>{stats.total}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>Total</div>
            </Card>
          </Col>
          <Col span={6}>
            <Card style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#faad14' }}>{stats.pending}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>En attente</div>
            </Card>
          </Col>
          <Col span={6}>
            <Card style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#13c2c2' }}>{stats.in_progress}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>En cours</div>
            </Card>
          </Col>
          <Col span={6}>
            <Card style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f5222d' }}>{stats.critical}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>Critiques</div>
            </Card>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={depannages}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1500 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} dépannages`
          }}
        />
      </Card>

      {/* Modal Création/Édition */}
      <Modal
        title={editMode ? 'Modifier le dépannage' : 'Nouveau dépannage'}
        open={openModal}
        onCancel={() => {
          setOpenModal(false);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Titre"
            name="title"
            rules={[{ required: true, message: 'Le titre est requis' }]}
          >
            <Input placeholder="Titre de l'intervention" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'La description est requise' }]}
          >
            <TextArea rows={4} placeholder="Décrire le problème..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Type d'intervention"
                name="intervention_type"
              >
                <Select placeholder="Sélectionner">
                  {equipmentTypes.map(type => (
                    <Select.Option key={type} value={type}>{type}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Priorité"
                name="priority"
                rules={[{ required: true, message: 'La priorité est requise' }]}
              >
                <Select placeholder="Niveau de priorité">
                  {Object.keys(priorityMap).map(key => (
                    <Select.Option key={key} value={key}>
                      {priorityMap[key as keyof typeof priorityMap].label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <Space>
                <span>Client</span>
                <Button
                  type="link"
                  size="small"
                  icon={<UserAddOutlined />}
                  onClick={() => setCreateClientModal(true)}
                  style={{ padding: 0, height: 'auto' }}
                >
                  Créer un nouveau client
                </Button>
              </Space>
            }
            name="customer_id"
            rules={[{ required: true, message: 'Le client est requis' }]}
          >
            <Select
              showSearch
              placeholder="Sélectionner un client"
              loading={loadingOptions}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={customers.map(customer => ({
                value: customer.user_id,
                label: `${customer.first_name} ${customer.last_name}`,
                email: customer.email || customer.user?.email,
              }))}
              optionRender={(option) => (
                <div>
                  <div style={{ fontWeight: 500 }}>{option.label}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {option.data.email}
                  </div>
                </div>
              )}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Statut" name="status" initialValue="pending">
                <Select>
                  {Object.keys(statusMap).map(key => (
                    <Select.Option key={key} value={key}>
                      {statusMap[key as keyof typeof statusMap].label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label="Date d'intervention" 
                name="scheduled_date"
                rules={[{ required: true, message: 'La date est requise' }]}
              >
                <DatePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: '100%' }}
                  placeholder="Sélectionner une date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Technicien (optionnel)" name="technician_id">
            <Select
              showSearch
              placeholder="Sélectionner un technicien (optionnel)"
              loading={loadingOptions}
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={technicians.map(tech => ({
                value: tech.id, // Utiliser tech.id au lieu de tech.user_id
                label: `${tech.first_name} ${tech.last_name}`,
                email: tech.email,
                specialization: tech.specialization,
              }))}
              optionRender={(option) => (
                <div>
                  <div style={{ fontWeight: 500 }}>{option.label}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {option.data.email}
                    {option.data.specialization && ` • ${option.data.specialization}`}
                  </div>
                </div>
              )}
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setOpenModal(false)}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editMode ? 'Modifier' : 'Créer'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Détails */}
      <Modal
        title="Détails du dépannage"
        open={viewModal}
        onCancel={() => setViewModal(false)}
        footer={[
          <Button key="close" onClick={() => setViewModal(false)}>
            Fermer
          </Button>
        ]}
        width={700}
      >
        {selectedDepannage && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ID">#{selectedDepannage.id}</Descriptions.Item>
            <Descriptions.Item label="Statut">
              {(() => {
                const statusInfo = statusMap[selectedDepannage.status as keyof typeof statusMap];
                if (!statusInfo) {
                  return <Tag color="default">{selectedDepannage.status || 'Inconnu'}</Tag>;
                }
                return (
                  <Tag color={statusInfo.color}>
                    {statusInfo.label}
                  </Tag>
                );
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Titre" span={2}>
              <strong>{selectedDepannage.title}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {selectedDepannage.description}
            </Descriptions.Item>
            <Descriptions.Item label="Client">
              {selectedDepannage.customer 
                ? `${selectedDepannage.customer.first_name} ${selectedDepannage.customer.last_name}`
                : 'Client inconnu'
              }
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedDepannage.customer?.email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Adresse" span={2}>
              {selectedDepannage.address || (selectedDepannage.customer ? 'Voir profil client' : 'Non spécifiée')}
            </Descriptions.Item>
            <Descriptions.Item label="Type d'intervention">
              {selectedDepannage.intervention_type || 'Non spécifié'}
            </Descriptions.Item>
            <Descriptions.Item label="Priorité">
              {(() => {
                const priorityInfo = priorityMap[selectedDepannage.priority as keyof typeof priorityMap];
                if (!priorityInfo) {
                  return <Tag color="default">{selectedDepannage.priority || 'Non définie'}</Tag>;
                }
                return (
                  <Tag color={priorityInfo.color}>
                    {priorityInfo.label}
                  </Tag>
                );
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Technicien" span={2}>
              {selectedDepannage.technician
                ? `${selectedDepannage.technician.first_name} ${selectedDepannage.technician.last_name}`
                : 'Non assigné'
              }
            </Descriptions.Item>
            <Descriptions.Item label="Contrat d'entretien">
              {selectedDepannage.contract_id ? (
                <Tag color="blue">Contrat #{selectedDepannage.contract_id}</Tag>
              ) : (
                <Tag color="default">Aucun contrat</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Coût diagnostic">
              {(selectedDepannage.is_free_diagnosis || Number(selectedDepannage.diagnostic_fee) === 0) ? (
                <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  <strong>GRATUIT</strong>
                </Tag>
              ) : (
                <Tag color="gold" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  <strong>{(Number(selectedDepannage.diagnostic_fee) || 4000).toLocaleString()} FCFA</strong>
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Date planifiée">
              {selectedDepannage.scheduled_date ? dayjs(selectedDepannage.scheduled_date).format('DD/MM/YYYY HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Date création">
              {dayjs(selectedDepannage.created_at).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Modal de création de client */}
      <Modal
        title={
          <Space>
            <UserAddOutlined />
            <span>Créer un nouveau client</span>
          </Space>
        }
        open={createClientModal}
        onCancel={() => {
          setCreateClientModal(false);
          clientForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={clientForm}
          layout="vertical"
          onFinish={handleCreateClient}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Prénom"
                name="first_name"
                rules={[{ required: true, message: 'Le prénom est requis' }]}
              >
                <Input placeholder="Prénom du client" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Nom"
                name="last_name"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input placeholder="Nom du client" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Email (optionnel)"
                name="email"
                rules={[
                  { type: 'email', message: 'Email invalide' }
                ]}
              >
                <Input placeholder="email@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Téléphone"
                name="phone"
                rules={[{ required: true, message: 'Le téléphone est requis' }]}
              >
                <Input placeholder="+225 XX XXX XX XX" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Adresse"
            name="address"
          >
            <Input placeholder="Adresse complète" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Ville"
                name="city"
              >
                <Input placeholder="Abidjan" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Commune"
                name="commune"
              >
                <Input placeholder="Cocody, Plateau, etc." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setCreateClientModal(false);
                clientForm.resetFields();
              }}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} icon={<UserAddOutlined />}>
                Créer le client
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DepannagePage;
